import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import {
  TimePickerMenuPosition,
  TimePickerSize,
  TimePickerState,
} from "./timePicker.types";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

export const COMPACT_INPUT_HEIGHT_PX = 40;
export const COMPACT_INPUT_WIDTH_PX = 128;
const DEFAULT_MENU_MAX_HEIGHT = 240;

export const Container = styled.div<{
  $fullWidth: boolean;
  $size: TimePickerSize;
}>`
  position: relative;
  width: ${(props) => {
    if (props.$fullWidth) return "100%";
    return props.$size === "compact"
      ? `${COMPACT_INPUT_WIDTH_PX}px`
      : `${V2_INPUT_WIDTH_PX}px`;
  }};
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

export const FieldWrapper = styled.div<{
  $state: TimePickerState;
  $size: TimePickerSize;
}>`
  display: flex;
  align-items: center;
  height: ${(props) =>
    props.$size === "compact"
      ? `${COMPACT_INPUT_HEIGHT_PX}px`
      : `${V2_INPUT_HEIGHT_PX}px`};
  padding: 0 16px;
  border-radius: ${(props) => (props.$size === "compact" ? "12px" : "16px")};
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;

  ${(props) =>
    props.$state === "active" &&
    css`
      border-color: ${ColorV2.Blue1};
      box-shadow: 0 6px 16px rgba(15, 42, 64, 0.12);
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      border-color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${ColorV2.Blue7};
    `}
`;

export const Input = styled.input<{
  $state: TimePickerState;
  $size: TimePickerSize;
}>`
  ${TypographyV2.b2}
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  color: ${ColorV2.Blue1};

  &::placeholder {
    color: ${ColorV2.Blue5};
  }

  ${(props) =>
    props.$size === "compact" &&
    css`
      ${TypographyV2.s1}
    `}

  ${(props) =>
    props.$state === "inactive" &&
    css`
      color: ${ColorV2.Blue5};
    `}

  ${(props) =>
    props.$state === "active" &&
    css`
      color: ${ColorV2.Blue1};

      &::placeholder {
        color: ${ColorV2.Blue1};
      }
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      color: ${ColorV2.NegativeRed};

      &::placeholder {
        color: ${ColorV2.NegativeRed};
      }
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      color: ${ColorV2.Blue5};
      cursor: not-allowed;
    `}
`;

export const IconWrapper = styled.span<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;

  ${(props) =>
    !props.$disabled &&
    css`
      cursor: pointer;
    `}
`;

export const Menu = styled.div<{
  $isOpen: boolean;
  $zIndex: number;
  $size: TimePickerSize;
  $position: TimePickerMenuPosition | null;
}>`
  position: fixed;
  top: ${(props) => props.$position?.top ?? 0}px;
  left: ${(props) => props.$position?.left ?? 0}px;
  width: ${(props) => props.$position?.width ?? 0}px;
  margin: 0;
  padding: 12px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 12px 24px rgba(15, 42, 64, 0.12);
  z-index: ${(props) => props.$zIndex};
  display: ${(props) => (props.$isOpen ? "block" : "none")};

  ${(props) =>
    props.$size === "compact" &&
    css`
      min-width: 240px;
    `}
`;

export const MenuContent = styled.div<{ $size: TimePickerSize }>`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 8px;
  align-items: stretch;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  max-height: ${DEFAULT_MENU_MAX_HEIGHT}px;
`;

export const ColumnScroll = styled.div`
  max-height: ${DEFAULT_MENU_MAX_HEIGHT}px;
  overflow-y: auto;
  padding: 4px 0;
  padding-right: 8px;
  box-sizing: border-box;

  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${ColorV2.Blue7};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ColorV2.Blue6};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorV2.Blue5};
  }
`;

export const Divider = styled.span`
  width: 1px;
  background: ${ColorV2.Blue6};
  border-radius: 1px;
`;

export const Option = styled.div<{
  $isSelected?: boolean;
  $size: TimePickerSize;
}>`
  padding: 10px 12px;
  margin: 4px 4px;
  border-radius: 12px;
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  cursor: pointer;
  text-align: left;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  ${(props) =>
    props.$size === "compact" &&
    css`
      ${TypographyV2.s1}
      padding: 8px 10px;
      text-align: center;
    `}

  &:hover {
    background-color: ${ColorV2.Blue6};
    color: ${ColorV2.Blue1};
  }

  ${(props) =>
    props.$isSelected &&
    css`
      background-color: ${ColorV2.Blue1};
      color: ${ColorV2.White};
    `}
`;

export const Message = styled.span<{ $state: TimePickerState }>`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue5};

  ${(props) =>
    props.$state === "error" &&
    css`
      color: ${ColorV2.NegativeRed};
    `}
`;
